import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash';
import { forkJoin, Observable, Subject } from 'rxjs';
import { DEFAULT_SEAT_DATA } from '../../constants';
import { DragStates, RowSeatsOrders, ShapeKeys, ShapeTypes } from '../../enums';
import {
  IAuditoriumActionErrorResponse, IAuditoriumHistoryState,
  IBaseElement,
  IImageElement,
  IPriorityShape,
  ISeat,
  ISeatConnectionError,
  ISeatsPlanActionOptions,
  ISeatToSeatConnectionError,
  ISectorElement
} from '../../interfaces';
import {
  BaseElementManager,
  ConnectionsManager,
  ImageManager,
  RowManager,
  SeatManager,
  SectorManager,
  ShapeAttributesManager,
  ShapeManager
} from '../../managers';
import { GuidWrapper } from '../../types';
import { CreatorStateService } from './CreatorStateService';
import { CreatorCacheService } from './CreatorCacheService';
import { IZone } from 'src/app/prices/core/interfaces';

@Injectable()
export class CreatorActionsService {

  public static readonly createRowFromSelection$ = new Subject<{ seats: GuidWrapper<ISeat>[], prevSeats: GuidWrapper<ISeat>[] }>();
  public static readonly renumberSeats$ = new Subject<{ current: GuidWrapper<ISeat>[], previous: GuidWrapper<ISeat>[] }>();
  public static readonly redrawRows$ = new Subject<void>();
  public static readonly addSeatsToRow$ = new Subject<{
    previous: GuidWrapper<ISeat>[],
    current: GuidWrapper<ISeat>[]
  }>();
  public static readonly removeSeatsFromRow$ = new Subject<{
    previous: GuidWrapper<ISeat>[],
    current: GuidWrapper<ISeat>[]
  }>();

  public static readonly addSeats$ = new Subject<GuidWrapper<ISeat>[]>();
  public static readonly deleteSeats$ = new Subject<GuidWrapper<ISeat>[]>();
  public static readonly updateSeats$ = new Subject<GuidWrapper<ISeat>[]>();
  public static readonly removeSeatFromLoveSeat$ = new Subject<GuidWrapper<ISeat>[]>();

  public static readonly refreshConnections$ = new Subject<void>();

  public static readonly deleteBaseElements$ = new Subject<GuidWrapper<IBaseElement>[]>();
  public static readonly addBaseElements$ = new Subject<GuidWrapper<IBaseElement>[]>();
  public static readonly updateBaseElements$ = new Subject<GuidWrapper<IBaseElement>[]>();

  public static readonly addSectorElements$ = new Subject<GuidWrapper<ISectorElement>[]>();
  public static readonly updateSectors$ = new Subject<GuidWrapper<ISectorElement>[]>();
  public static readonly deleteSectors$ = new Subject<GuidWrapper<ISectorElement>[]>();

  public static readonly addImages$ = new Subject<GuidWrapper<IImageElement>[]>();
  public static readonly updateImages$ = new Subject<GuidWrapper<IImageElement>[]>();
  public static readonly deleteImages$ = new Subject<GuidWrapper<IImageElement>[]>();

  public static readonly changePriorityShape$ = new Subject<IPriorityShape>();
  public static readonly recountPriority$ = new Subject<void>();
  public static readonly changeElementsOpacity$ = new Subject<boolean>();

  public static readonly openEditZoneModal$ = new Subject<IZone>();

  public static readonly undo$ = new Subject<void>();
  public static readonly redo$ = new Subject<void>();
  public static readonly resetToDefault$ = new Subject<void>();
  public static readonly saveInCreatorHistory$ = new Subject<{
    current: IAuditoriumHistoryState,
    previous: IAuditoriumHistoryState
  }>();
  public static readonly clearConnections$ = new Subject<void>();
  public static readonly disconnectSeats$ = new Subject<{
    previous: GuidWrapper<ISeat>[],
    current: GuidWrapper<ISeat>[]
  }>();
  public static readonly connectSeatToSeat$ = new Subject<{
    previous: GuidWrapper<ISeat>[],
    current: GuidWrapper<ISeat>[]
  } | IAuditoriumActionErrorResponse<ISeatToSeatConnectionError>>();
  public static readonly connectSeats$ = new Subject<{
    previous: GuidWrapper<ISeat>[],
    current: GuidWrapper<ISeat>[]
  } | IAuditoriumActionErrorResponse<ISeatConnectionError>>();

  public static readonly priorityShapeRotationChanged$ = new Subject<{ previous: number, current: number }>();

  /**********************************************
   * Rows & Curves
   *****************************************
   */

  public static renumberSeats(
    seats: GuidWrapper<ISeat>[],
    direction?: 'horizontal' | 'vertical',
    order: RowSeatsOrders = RowSeatsOrders.LEFT_TO_RIGHT,
    startNumber: number = 1,
    options?: ISeatsPlanActionOptions
  ): {
    previous: GuidWrapper<ISeat>[],
    current: GuidWrapper<ISeat>[]
  } {
    const previous: GuidWrapper<ISeat>[] = cloneDeep(seats);
    let current: GuidWrapper<ISeat>[] = [];

    const seatsWithRanks = RowManager.formatSeatsToSeatsSeatsWithRanks(seats);
    
    for (const seatsWithRank of seatsWithRanks) {
      current.push(...RowManager.renumberSeats(seatsWithRank.seats, startNumber, direction, order));
      RowManager.updateRowLine(seatsWithRank.rankToken);
    }
    
    const res = {
      previous,
      current
    };

    if (options?.emitEvent) {
      this.renumberSeats$.next(res);
    }

    return res;
  }

  public static redrawRows(options?: ISeatsPlanActionOptions): void {
    if (options?.emitEvent) {
      this.redrawRows$.next();
    }
  }

  public static removeSeatsFromRow(seats: GuidWrapper<ISeat>[], options?: ISeatsPlanActionOptions): {
    previous: GuidWrapper<ISeat>[],
    current: GuidWrapper<ISeat>[]
  } {
    const previous: GuidWrapper<ISeat>[] = [];
    const current: GuidWrapper<ISeat>[] = [];

    for (const seat of seats) {
      previous.push(cloneDeep(seat));

      const prevRankToken = seat.rankToken;
      const fIndex = CreatorStateService.seatsPlan.seats.findIndex((s) => s.guid === seat.guid);

      seat.seatNumber = 0;
      seat.rank = DEFAULT_SEAT_DATA.rank;
      seat.rankToken = DEFAULT_SEAT_DATA.rankToken;

      CreatorStateService.seatsPlan.seats[fIndex] = seat;
      current.push(seat);

      RowManager.renumberSeatsInRow(prevRankToken);
    }

    if (options?.emitEvent) {
      this.removeSeatsFromRow$.next({ previous, current });
    }

    return { previous, current };
  }

  public static addSeatsToRow(
    seats: GuidWrapper<ISeat>[],
    rank: number,
    rankToken: string,
    options?: ISeatsPlanActionOptions
  ): {
    previous: GuidWrapper<ISeat>[],
    current: GuidWrapper<ISeat>[]
  } {
    for (const seat of seats) {
      const fIndex = CreatorStateService.seatsPlan.seats.findIndex((s) => s.guid === seat.guid);
      seat.rank = rank;
      seat.rankToken = rankToken;

      CreatorStateService.seatsPlan.seats[fIndex] = seat;
    }

    const res = {
      previous: cloneDeep(RowManager.getRowSeats(rankToken)).map(x => {
        if (x.seatNumber === 0) {
          x.rankToken = DEFAULT_SEAT_DATA.rankToken;
          x.rank = DEFAULT_SEAT_DATA.rank;
        }

        return x;
      }),
      current: cloneDeep(RowManager.renumberSeatsInRow(rankToken)),
    };

    if (options?.emitEvent) {
      this.addSeatsToRow$.next(res);
    }

    return res;
  }

  public static createRowFromSelection(options?: ISeatsPlanActionOptions): {
    seats: GuidWrapper<ISeat>[],
    prevSeats: GuidWrapper<ISeat>[]
  } {
    const elements = Array.from<HTMLElement>(
      CreatorStateService.roomRef.nativeElement.querySelectorAll(
        `app-shape[${ShapeAttributesManager.getAttribute(ShapeKeys.DRAG_STATE)}="${DragStates.SELECTED}"]`
      )
    ).filter(x => ShapeAttributesManager.getShapeType(x) === ShapeTypes.PLACE_SHAPE);

    let res: { seats: GuidWrapper<ISeat>[], prevSeats: GuidWrapper<ISeat>[] } = null;

    if (elements?.length) {
      let seats: GuidWrapper<ISeat>[] = elements.map(x => SeatManager.getSeat(ShapeAttributesManager.getGUID(x)));
      const rank = RowManager.currentRowRank + 1;
      const prevSeats = cloneDeep(seats);

      const rankToken = `${rank}`;

      seats = seats.map(x => ({
        ...x,
        rank,
        rankToken
      }));

      CreatorActionsService.updateSeats(seats);
      RowManager.updateRowLine(rankToken);
      RowManager.renumberSeatsInRow(rankToken);

      res = {seats, prevSeats};
    }

    if (options?.emitEvent) {
      this.createRowFromSelection$.next(res);
    }

    return res;
  }

  /**********************************************
   * SEATS
   *****************************************
   */

  public static addSeats(seatElements: HTMLElement[] | GuidWrapper<ISeat>[], options?: ISeatsPlanActionOptions): GuidWrapper<ISeat>[] {
    const createdSeats: GuidWrapper<ISeat>[] = [];

    for (const seatsElement of seatElements) {
      if (seatsElement instanceof HTMLElement) {
        const seat = SeatManager.transformHTMLElementToObject(seatsElement);
        createdSeats.push(seat);
        CreatorStateService.seatsPlan.seats.push(seat);

        if (seat?.sectorElement?.id) {
          CreatorStateService.deleteElementsIds
            = CreatorStateService.deleteElementsIds.filter(id => id !== seat.sectorElement.id);
        }
      } else {
        createdSeats.push(seatsElement);
        CreatorStateService.seatsPlan.seats.push(seatsElement);

        if (seatsElement?.sectorElement?.id) {
          CreatorStateService.deleteElementsIds
            = CreatorStateService.deleteElementsIds.filter(id => id !== seatsElement.sectorElement.id);
        }
      }
    }

    CreatorStateService.emitSeatsPlanStateChange();

    if (options?.emitEvent) {
      this.addSeats$.next(createdSeats);
    }

    return createdSeats;
  }

  public static updateSeats(seatElements: HTMLElement[] | GuidWrapper<ISeat>[], options?: ISeatsPlanActionOptions): GuidWrapper<ISeat>[] {
    const updatedSeats: GuidWrapper<ISeat>[] = [];
    

    for (let seatElement of seatElements) {
      if (seatElement instanceof HTMLElement) {
        const seat = SeatManager.transformHTMLElementToObject(seatElement);
        updatedSeats.push(seat);

        const seatIndex = CreatorStateService.seatsPlan.seats.findIndex((s) => s.guid === seat.guid);

        CreatorStateService.seatsPlan.seats[seatIndex] = seat;
      } else {
        updatedSeats.push(seatElement as GuidWrapper<ISeat>);

        const seatIndex = CreatorStateService.seatsPlan.seats.findIndex((s) => s.guid === (seatElement as GuidWrapper<ISeat>).guid);
        CreatorStateService.seatsPlan.seats[seatIndex] = seatElement;
      }
    }

    if (options?.emitEvent) {
      this.updateSeats$.next(updatedSeats);
    }

    return updatedSeats;
  }

  public static refreshConnections(options?: ISeatsPlanActionOptions): void {
    if (options?.emitEvent) {
      this.refreshConnections$.next();
    }
  }

  public static deleteSeats(data: HTMLElement[] | GuidWrapper<ISeat>[], options?: ISeatsPlanActionOptions): GuidWrapper<ISeat>[] {
    const deletedSeats: GuidWrapper<ISeat>[] = [];

    for (const el of data) {
      if (el instanceof HTMLElement) {
        const deletedSeat = CreatorStateService.seatsPlan.seats.find((s) => s.guid === ShapeAttributesManager.getGUID(el));
        deletedSeats.push(deletedSeat);
        ShapeManager.remove(el);

        if (deletedSeat?.sectorElement?.id && !CreatorStateService.deleteElementsIds.find(id => id === deletedSeat.sectorElement.id)) {
          CreatorStateService.deleteElementsIds.push(deletedSeat.sectorElement.id);
        }
      } else {
        deletedSeats.push(el);

        const seatElement = SeatManager.getHTMLElement(el.guid);

        if (seatElement) {
          ShapeManager.remove(seatElement);
        }

        if (el?.sectorElement?.id && !CreatorStateService.deleteElementsIds.find(id => id === el?.sectorElement?.id)) {
          CreatorStateService.deleteElementsIds.push(el?.sectorElement?.id);
        }
      }
    }

    CreatorStateService.seatsPlan.seats = CreatorStateService.seatsPlan.seats
      .filter(x => !deletedSeats.find(y => y.guid === x.guid));
    CreatorStateService.emitSeatsPlanStateChange();

    if (options?.emitEvent) {
      this.deleteSeats$.next(deletedSeats);
    }

    return deletedSeats;
  }

  public static removeSeatFromLoveSeat(data: HTMLElement[] | GuidWrapper<ISeat>[], options?: ISeatsPlanActionOptions): GuidWrapper<ISeat>[] {
    const deletedSeats = CreatorActionsService.deleteSeats(data)

    if (options?.emitEvent) {
      this.removeSeatFromLoveSeat$.next(deletedSeats);
    }

    return deletedSeats;
  }

  /**********************************************
   * BASE ELEMENTS
   *****************************************
   */

  public static addBaseElements(baseElements: HTMLElement[] | GuidWrapper<IBaseElement>[], options?: ISeatsPlanActionOptions): GuidWrapper<IBaseElement>[] {
    const createdBaseElements: GuidWrapper<IBaseElement>[] = [];

    for (const baseElement of baseElements) {
      if (baseElement instanceof HTMLElement) {
        const el = BaseElementManager.transformHTMLElementToObject(baseElement);
        createdBaseElements.push(el);
        CreatorStateService.seatsPlan.baseElements.push(el);

        if (el?.id) {
          CreatorStateService.deleteElementsIds
            = CreatorStateService.deleteElementsIds.filter(x => x !== el.id);
        }
      } else {
        createdBaseElements.push(baseElement);
        CreatorStateService.seatsPlan.baseElements.push(baseElement);

        if (baseElement?.id) {
          CreatorStateService.deleteElementsIds
            = CreatorStateService.deleteElementsIds.filter(x => x !== baseElement.id);
        }
      }
    }

    CreatorStateService.emitSeatsPlanStateChange();

    if (options?.emitEvent) {
      this.addBaseElements$.next(createdBaseElements);
    }

    return createdBaseElements;
  }

  public static deleteBaseElements(data: HTMLElement[] | GuidWrapper<IBaseElement>[], options?: ISeatsPlanActionOptions): GuidWrapper<IBaseElement>[] {
    const deletedBaseElements: GuidWrapper<IBaseElement>[] = [];

    for (const el of data) {
      if (el instanceof HTMLElement) {
        const deletedEl = CreatorStateService.seatsPlan.baseElements.find((s) => s.guid === ShapeAttributesManager.getGUID(el));

        if (deletedEl) {
          deletedBaseElements.push(deletedEl);
          ShapeManager.remove(el);

          if (deletedEl?.id && !CreatorStateService.deleteElementsIds.includes(deletedEl.id)) {
            CreatorStateService.deleteElementsIds.push(deletedEl.id);
          }
        }
      } else {
        deletedBaseElements.push(el);
        const baseElement = BaseElementManager.getHTMLElement(el.guid);

        if (baseElement) {
          ShapeManager.remove(baseElement);
        }

        if (el?.id && !CreatorStateService.deleteElementsIds.includes(el.id)) {
          CreatorStateService.deleteElementsIds.push(el.id);
        }
      }
    }

    CreatorStateService.seatsPlan.baseElements = CreatorStateService.seatsPlan.baseElements
      .filter(x => !deletedBaseElements.find(y => y.guid === x.guid));
    CreatorStateService.emitSeatsPlanStateChange();

    if (options?.emitEvent) {
      this.deleteBaseElements$.next(deletedBaseElements);
    }

    return deletedBaseElements;
  }

  public static updateBaseElements(baseElements: HTMLElement[] | GuidWrapper<IBaseElement>[], options?: ISeatsPlanActionOptions): GuidWrapper<IBaseElement>[] {
    const updatedBaseElements: GuidWrapper<IBaseElement>[] = [];

    for (let baseEl of baseElements) {
      if (baseEl instanceof HTMLElement) {
        const el = BaseElementManager.transformHTMLElementToObject(baseEl);
        updatedBaseElements.push(el);

        const elIndex = CreatorStateService.seatsPlan.baseElements.findIndex((s) => s.guid === el.guid);

        CreatorStateService.seatsPlan.baseElements[elIndex] = el;
      } else {
        updatedBaseElements.push(baseEl as GuidWrapper<IBaseElement>);

        const elIndex = CreatorStateService.seatsPlan.baseElements.findIndex((s) => s.guid === (baseEl as GuidWrapper<IBaseElement>).guid);
        CreatorStateService.seatsPlan.baseElements[elIndex] = baseEl;
      }
    }

    if (options?.emitEvent) {
      this.updateBaseElements$.next(updatedBaseElements);
    }

    return updatedBaseElements;
  }

  /**********************************************
   * SECTORS
   *****************************************
   */

  public static addSectors(sectorElements: GuidWrapper<ISectorElement>[] | HTMLElement[], options?: ISeatsPlanActionOptions): GuidWrapper<ISectorElement>[] {
    const createdSectors: GuidWrapper<ISectorElement>[] = [];

    for (const sectorElement of sectorElements) {
      if (sectorElement instanceof HTMLElement) {
        const el = SectorManager.transformHTMLElementToObject(sectorElement);

        createdSectors.push(el);
        CreatorStateService.seatsPlan.sectors.push(el);

        if (el?.id) {
          CreatorStateService.deleteElementsIds = CreatorStateService.deleteElementsIds.filter(x => x !== el.id);
        }
      } else {
        createdSectors.push(sectorElement);
        CreatorStateService.seatsPlan.sectors.push(sectorElement);

        if (sectorElement?.id) {
          CreatorStateService.deleteElementsIds = CreatorStateService.deleteElementsIds.filter(x => x !== sectorElement.id);
        }
      }
    }

    CreatorStateService.emitSeatsPlanStateChange();

    if (options?.emitEvent) {
      this.addSectorElements$.next(createdSectors);
    }

    return createdSectors;
  }

  public static updateSectors(sectorElements: HTMLElement[] | GuidWrapper<ISectorElement>[], options?: ISeatsPlanActionOptions): GuidWrapper<ISectorElement>[] {
    const updatedSectors: GuidWrapper<ISectorElement>[] = [];

    for (let sectorEl of sectorElements) {
      if (sectorEl instanceof HTMLElement) {
        const el = SectorManager.transformHTMLElementToObject(sectorEl);
        updatedSectors.push(el);

        const elIndex = CreatorStateService.seatsPlan.sectors.findIndex((s) => s.guid === el.guid);

        CreatorStateService.seatsPlan.sectors[elIndex] = el;
      } else {
        updatedSectors.push(sectorEl as GuidWrapper<ISectorElement>);

        const elIndex = CreatorStateService.seatsPlan.sectors.findIndex(
          (s) => s.guid === (sectorEl as GuidWrapper<ISectorElement>).guid
        );
        CreatorStateService.seatsPlan.sectors[elIndex] = sectorEl;
      }
    }

    if (options?.emitEvent) {
      this.updateSectors$.next(updatedSectors);
    }

    return updatedSectors;
  }

  public static deleteSectors(data: HTMLElement[] | GuidWrapper<ISectorElement>[], options?: ISeatsPlanActionOptions): GuidWrapper<ISectorElement>[] {
    const deletedSectors: GuidWrapper<ISectorElement>[] = [];

    for (const el of data) {
      if (el instanceof HTMLElement) {
        const deletedEl = CreatorStateService.seatsPlan.sectors.find((s) => s.guid === ShapeAttributesManager.getGUID(el));
        deletedSectors.push(deletedEl);
        ShapeManager.remove(el);

        if (deletedEl?.id && !CreatorStateService.deleteElementsIds.includes(deletedEl.id)) {
          CreatorStateService.deleteElementsIds.push(deletedEl.id);
        }
      } else {
        deletedSectors.push(el);
        const baseElement = BaseElementManager.getHTMLElement(el.guid);

        if (baseElement) {
          ShapeManager.remove(baseElement);
        }

        if (el?.id && !CreatorStateService.deleteElementsIds.includes(el.id)) {
          CreatorStateService.deleteElementsIds.push(el.id);
        }
      }
    }

    CreatorStateService.seatsPlan.sectors = CreatorStateService.seatsPlan.sectors
      .filter(x => !deletedSectors.find(y => y.guid === x.guid));
    CreatorStateService.emitSeatsPlanStateChange();

    if (options?.emitEvent) {
      this.deleteSectors$.next(deletedSectors);
    }

    return deletedSectors;
  }

  /**********************************************
   * IMAGES
   *****************************************
   */

  public static addImages(images: GuidWrapper<IImageElement>[], options?: ISeatsPlanActionOptions): GuidWrapper<IImageElement>[] {
    CreatorStateService.seatsPlan.images = [...CreatorStateService.seatsPlan.images, ...images];
    CreatorStateService.emitSeatsPlanStateChange();

    if (options?.emitEvent) {
      this.addImages$.next(images);
    }

    return images;
  }

  public static updateImages(images: (HTMLElement | GuidWrapper<IImageElement>)[], options?: ISeatsPlanActionOptions): GuidWrapper<IImageElement>[] {
    const updatedImages: GuidWrapper<IImageElement>[] = [];

    for (let image of images) {
      if (image instanceof HTMLElement) {
        const imageEl = ImageManager.transformHTMLElementToObject(image);
        updatedImages.push(imageEl);

        const imageIndex = CreatorStateService.seatsPlan.images.findIndex((s) => s.guid === imageEl.guid);

        CreatorStateService.seatsPlan.images[imageIndex] = imageEl;
      } else {
        updatedImages.push(image);

        const imgIndex = CreatorStateService.seatsPlan.images.findIndex((s) => s.guid === (image as GuidWrapper<IImageElement>).guid);
        CreatorStateService.seatsPlan.images[imgIndex] = image;
      }
    }

    if (options?.emitEvent) {
      this.updateImages$.next(updatedImages);
    }

    return updatedImages;
  }

  public static deleteImages(images: (HTMLElement | GuidWrapper<IImageElement>)[], options?: ISeatsPlanActionOptions): GuidWrapper<IImageElement>[] {
    const deletedImages: GuidWrapper<IImageElement>[] = [];

    for (const image of images) {
      if (image instanceof HTMLElement) {
        const deletedImage = CreatorStateService.seatsPlan.images.find((s) => s.guid === ShapeAttributesManager.getGUID(image));
        deletedImages.push(deletedImage);
        ShapeManager.remove(image);

        CreatorStateService.seatsPlan.images = CreatorStateService.seatsPlan.images.filter(x => x.guid !== deletedImage.guid);
      } else {
        deletedImages.push(image);

        const imageElement = ImageManager.getHTMLElement(image.guid);

        if (imageElement) {
          ShapeManager.remove(imageElement);
        }

        CreatorStateService.seatsPlan.images = CreatorStateService.seatsPlan.images.filter(x => x.guid !== image.guid);
      }
    }

    if (options?.emitEvent) {
      this.deleteImages$.next(deletedImages);
    }

    return deletedImages;
  }

  /**********************************************
   * OTHER
   *****************************************
   */

  public static resetToDefault(): void {
    CreatorActionsService.resetToDefault$.next();
  }

  public static clearConnections(options?: ISeatsPlanActionOptions): void {
    if (options?.emitEvent) {
      this.clearConnections$.next();
    }
  }

  public static disconnectSeats(seats: GuidWrapper<ISeat>[], options?: ISeatsPlanActionOptions): {
    previous: GuidWrapper<ISeat>[],
    current: GuidWrapper<ISeat>[]
  } {
    const ids = seats.map(x => x.id);
    const previous = cloneDeep(seats);
    const current: GuidWrapper<ISeat>[] = [];

    for (const seat of seats) {
      if (seat.rightSeatDefinitionId && ids.includes(seat.rightSeatDefinitionId)) {
        ConnectionsManager.deleteConnectionLine(seat.id, seat.rightSeatDefinitionId);
        seat.rightSeatDefinitionId = null;
      }

      if (seat.leftSeatDefinitionId && ids.includes(seat.leftSeatDefinitionId)) {
        ConnectionsManager.deleteConnectionLine(seat.id, seat.leftSeatDefinitionId);
        seat.leftSeatDefinitionId = null;
      }

      const seatIndex = CreatorStateService.seatsPlan.seats.findIndex((s) => s.guid === seat.guid);
      CreatorStateService.seatsPlan.seats[seatIndex] = seat;
      current.push(seat);
    }

    if (options?.emitEvent) {
      this.disconnectSeats$.next({ current, previous });
    }

    return { current, previous };
  }

  public static connectSeats(
    seats: GuidWrapper<ISeat>[],
    dir?: 'horizontally' | 'vertically',
    options?: ISeatsPlanActionOptions
  ): {
    previous: GuidWrapper<ISeat>[],
    current: GuidWrapper<ISeat>[]
  } | IAuditoriumActionErrorResponse<ISeatConnectionError> {
    const previous = cloneDeep(seats);

    const errorRes: IAuditoriumActionErrorResponse<ISeatConnectionError> = {
      error: true,
      additionalData: {
        seats,
        dir: dir ?? null
      }
    };

    if (!dir) {
      if (this.areSeatsInDifferentHeights(seats)) {
        this.connectSeats$.next(errorRes);
        return errorRes;
      }

      seats = seats.sort((a, b) => a.sectorElement.webX - b.sectorElement.webX);
    } else if (dir === 'vertically') {
      seats = seats.sort((a, b) => {
        if (a.sectorElement.webY === b.sectorElement.webY) {
          return a.sectorElement.webX - b.sectorElement.webX;
        }
        return a.sectorElement.webY - b.sectorElement.webY;
      });
    } else if (dir === 'horizontally') {
      seats = seats.sort((a, b) => a.sectorElement.webX - b.sectorElement.webX);
    }

    const current: GuidWrapper<ISeat>[] = [];

    for (let i = 0; i < seats.length - 1; i++) {
      const seat = seats[i];
      const nextSeat = seats[i + 1];

      if (dir === 'vertically' || (!dir && Math.abs(nextSeat.sectorElement.webY - seat.sectorElement.webY) < Math.abs(nextSeat.sectorElement.webX - seat.sectorElement.webX))) {
        if (seat.rightSeatDefinitionId || nextSeat.leftSeatDefinitionId) {
          this.connectSeats$.next(errorRes);
          return errorRes;
        }

        seat.rightSeatDefinitionId = nextSeat.id;
        nextSeat.leftSeatDefinitionId = seat.id;
      } else {
        if (seat.rightSeatDefinitionId || nextSeat.leftSeatDefinitionId) {
          this.connectSeats$.next(errorRes);
          return errorRes;
        }

        seat.rightSeatDefinitionId = nextSeat.id;
        nextSeat.leftSeatDefinitionId = seat.id;
      }

      const { firstIndex, secondIndex } = this.getSeatsIndexes({ firstSeat: seat, secondSeat: nextSeat });

      CreatorStateService.seatsPlan.seats[firstIndex] = seat;
      CreatorStateService.seatsPlan.seats[secondIndex] = nextSeat;

      let canFindSeat = false;
      let canFindNextSeat = false;

      for (const currentSeat of current) {
        if (currentSeat.guid === seat.guid) {
          canFindSeat = true;
        } else if (currentSeat.guid === nextSeat.guid) {
          canFindNextSeat = true;
        }
      }

      if (!canFindSeat) {
        current.push(seat);
      }

      if (!canFindNextSeat) {
        current.push(nextSeat);
      }

      ConnectionsManager.drawLine(seat, nextSeat);
    }

    const res = { current, previous };

    if (options?.emitEvent) {
      this.connectSeats$.next(res);
    }

    return res;
  }

  public static areSeatsInDifferentHeights(seats: GuidWrapper<ISeat>[]): boolean {
    const yCoords = seats.map(seat => seat.sectorElement.webY);
    const maxY = Math.max(...yCoords);
    const minY = Math.min(...yCoords);
    const seatHeight = seats[0].sectorElement.h;

    return maxY - minY > seatHeight / 2;
  }

  public static saveInCreatorHistory(data: {
    current: IAuditoriumHistoryState,
    previous: IAuditoriumHistoryState
  }): void {
    this.saveInCreatorHistory$.next(data);
  }

  public static undo(): void {
    this.undo$.next();
  }

  public static redo(): void {
    this.redo$.next();
  }

  public static setAllSeatsToZeroPriority(): void {
    const weight = 0;

    CreatorStateService.seatsPlan.seats.forEach((seat) => {
      seat.seatWeight = weight;
      CreatorStateService.seatsGuidsWithManualPriority.push(seat.guid);
    });
    CreatorStateService.seatsElements.forEach((el) => this.updateSeatElementPriority(el, weight));
  }

  public static priorityShapeRotationChanged({ previous, current }: { previous: number, current: number }, options?: ISeatsPlanActionOptions): void {
    if (options?.emitEvent) {
      this.priorityShapeRotationChanged$.next({ previous, current });
    }
  }

  public static recountPriority(maxPriority: number, options?: ISeatsPlanActionOptions): void {
    const priorityShape = CreatorStateService.priorityShape;

    const centerX = priorityShape.x + priorityShape.w / 2;
    const centerY = priorityShape.y + priorityShape.h / 2;
    const { x, y, w, h, rotate, shape } = priorityShape;

    const distance = (x1: number, y1: number, x2: number, y2: number) => Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    const rotatePoint = (cx: number, cy: number, angle: number, p: { x: number, y: number }) => {
      const radians = (Math.PI / 180) * angle;
      const cos = Math.cos(radians);
      const sin = Math.sin(radians);
      const nx = (cos * (p.x - cx)) + (sin * (p.y - cy)) + cx;
      const ny = (cos * (p.y - cy)) - (sin * (p.x - cx)) + cy;
      return { x: nx, y: ny };
    };
    const isPointInPolygon = (point: { x: number, y: number }, polygon: { x: number, y: number }[]) => {
      let isInside = false;
      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x, yi = polygon[i].y;
        const xj = polygon[j].x, yj = polygon[j].y;
        const intersect = ((yi > point.y) !== (yj > point.y)) && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);

        if (intersect) isInside = !isInside;
      }
      return isInside;
    };
    const getSeatCorners = (seat: GuidWrapper<ISeat>) => {
      const { webX: seatX, webY: seatY, w: seatW, h: seatH } = seat.sectorElement;
      return [
        { x: seatX, y: seatY },
        { x: seatX + seatW, y: seatY },
        { x: seatX, y: seatY + seatH },
        { x: seatX + seatW, y: seatY + seatH },
      ];
    };
    const isInShape = (seat: GuidWrapper<ISeat>): boolean => {
      const seatCenterX = seat.sectorElement.webX + seat.sectorElement.w / 2;
      const seatCenterY = seat.sectorElement.webY + seat.sectorElement.h / 2;
      const rotatedSeatCenter = rotatePoint(x + w / 2, y + h / 2, rotate, { x: seatCenterX, y: seatCenterY });
      const seatCorners = getSeatCorners(seat).map(corner => rotatePoint(x + w / 2, y + h / 2, rotate, corner));
      const points = shape.match(/(\d+(\.\d+)?% \d+(\.\d+)?%)/g).map(point => {
        const [px, py] = point.split(' ').map(p => parseFloat(p) / 100);
        return { x: x + px * w, y: y + py * h };
      });

      return [rotatedSeatCenter, ...seatCorners].some(corner => isPointInPolygon(corner, points));
    };

    const seatsWithDistances: {
      seat: GuidWrapper<ISeat>,
      seatElement: HTMLElement,
      distance: number,
      inShape: boolean
    }[] = [];

    for (const seatElement of CreatorStateService.seatsElements) {
      const seatGuid = ShapeAttributesManager.getGUID(seatElement);

      if (CreatorStateService.seatsGuidsWithManualPriority.includes(seatGuid)) {
        continue;
      }

      const seat = SeatManager.getSeat(seatGuid);
      const seatCenterX = seat.sectorElement.webX + seat.sectorElement.w / 2;
      const seatCenterY = seat.sectorElement.webY + seat.sectorElement.h / 2;
      seatsWithDistances.push({
        seat,
        seatElement,
        distance: distance(centerX, centerY, seatCenterX, seatCenterY),
        inShape: isInShape(seat)
      });
    }

    seatsWithDistances.sort((a, b) => {
      if (a.inShape && !b.inShape) return -1;
      if (!a.inShape && b.inShape) return 1;
      return a.distance - b.distance;
    });

    const updatedSeats: GuidWrapper<ISeat>[] = [];
    let currentPriority = maxPriority;

    seatsWithDistances.forEach((data) => {
      data.seat.seatWeight = currentPriority;

      this.updateSeatElementPriority(data.seatElement, currentPriority);

      updatedSeats.push(data.seat);

      if (currentPriority > 1) {
        currentPriority--;
      }
    });

    CreatorActionsService.updateSeats(updatedSeats);

    if (options?.emitEvent) {
      this.recountPriority$.next();
    }
  }

  public static changePriorityShape(shape: IPriorityShape, options?: ISeatsPlanActionOptions): IPriorityShape {
    CreatorStateService.priorityShape = shape;

    if (options?.emitEvent) {
      this.changePriorityShape$.next(CreatorStateService.priorityShape);
    }

    return CreatorStateService.priorityShape;
  }

  public static updateSeatElementPriority(el: HTMLElement, priority: number): void {
    const seatTextElement = el.querySelector<HTMLElement>('[data-text-container]');

    if (seatTextElement) {
      seatTextElement.innerHTML = priority.toString();
      const parentElement = seatTextElement.parentElement;

      if (parentElement) {
        parentElement.style.setProperty('--character-count', priority.toString().length.toString());
      }
    }
  }

  public static changeElementsOpacity(showOpacity: boolean, options?: ISeatsPlanActionOptions): boolean {
    CreatorStateService.roomRef.nativeElement.querySelectorAll<HTMLElement>('app-shape').forEach((el) => {
      el.style.opacity = showOpacity ? '0.5' : '1';
    });

    if (options?.emitEvent) {
      this.changeElementsOpacity$.next(showOpacity);
    }

    return showOpacity;
  }

  public static connectSeatToSeat(seat: GuidWrapper<ISeat>, seatToConnect: GuidWrapper<ISeat>, dir: 'left' | 'right', options?: ISeatsPlanActionOptions): {
    previous: GuidWrapper<ISeat>[],
    current: GuidWrapper<ISeat>[]
  } | IAuditoriumActionErrorResponse<ISeatToSeatConnectionError> {
    if ((!seat || !seatToConnect )|| (seat?.id === seatToConnect.id)) return null;

    const previous = cloneDeep([seat, seatToConnect]);
    const errorRes: IAuditoriumActionErrorResponse<ISeatToSeatConnectionError> = {
      error: true,
      additionalData: {
        dir,
        seat,
        seatToConnect
      }
    };

    if (dir === 'left') {
      if (seatToConnect.rightSeatDefinitionId) {
        this.connectSeatToSeat$.next(errorRes);
        return errorRes;
      }

      seat.leftSeatDefinitionId = seatToConnect.id;
      seatToConnect.rightSeatDefinitionId = seat.id;
    } else {
      if (seatToConnect.leftSeatDefinitionId) {
        this.connectSeatToSeat$.next(errorRes);
        return errorRes;
      }

      seat.rightSeatDefinitionId = seatToConnect.id;
      seatToConnect.leftSeatDefinitionId = seat.id;
    }

    const { firstIndex, secondIndex } = this.getSeatsIndexes({ firstSeat: seat, secondSeat: seatToConnect });

    CreatorStateService.seatsPlan.seats[firstIndex] = seat;
    CreatorStateService.seatsPlan.seats[secondIndex] = seatToConnect;

    const current = cloneDeep([seat, seatToConnect]);

    ConnectionsManager.drawLine(seat, seatToConnect);

    const res = {
      previous,
      current
    };

    if (options?.emitEvent) {
      this.connectSeatToSeat$.next(res);
    }

    return res;
  }

  private static getSeatsIndexes(data: { firstSeat: GuidWrapper<ISeat>, secondSeat: GuidWrapper<ISeat> }): { firstIndex: number, secondIndex: number } {
    let firstIndex: number, secondIndex: number;
    const seatsPlanSeats = CreatorStateService.seatsPlan.seats;

    for (let i = 0; i < seatsPlanSeats.length; i++) {
      const seatsPlanSeat = seatsPlanSeats[i];

      if (seatsPlanSeat.guid === data.firstSeat.guid) {
        firstIndex = i;
      } else if (seatsPlanSeat.guid === data.secondSeat.guid) {
        secondIndex = i;
      }
    }

    return {
      firstIndex,
      secondIndex
    };
  }

  public static checkSeatsHaveSameNumbers(id?: number): boolean {
    const seatsPlan = id
      ? (CreatorCacheService.seatsPlansCacheMap.get(id)?.seatsPlan ?? CreatorStateService.seatsPlan)
      : CreatorStateService.seatsPlan;

    const seatsWithRanks = RowManager.formatSeatsToSeatsSeatsWithRanks(seatsPlan.seats);
    let haveErrors = false;
    
    const obs: Observable<HTMLElement>[] = [];
    const previousSeatsWithElements: { seat: GuidWrapper<ISeat>, element: HTMLElement }[] = [];
    const currentSeatsWithElements: { seat: GuidWrapper<ISeat>, element: HTMLElement }[] = [];

    if (seatsPlan === CreatorStateService.seatsPlan) {
      const shapesElementsWithError = Array.from(CreatorStateService.roomRef.nativeElement.querySelectorAll<HTMLElement>('[data-shape-has-error="true"]'));

      for (const shapeElement of shapesElementsWithError) {
        previousSeatsWithElements.push({ seat: SeatManager.getSeat(ShapeAttributesManager.getGUID(shapeElement)), element: shapeElement });
      }
    }
    
    for (const rankedSeats of seatsWithRanks) {
      const numberMap = new Map<number, GuidWrapper<ISeat>[]>();
      
      for (const seat of rankedSeats.seats) {
        if (seat.seatNumber === 0) continue;
        
        if (!numberMap.has(seat.seatNumber)) {
          numberMap.set(seat.seatNumber, []);
        }
        numberMap.get(seat.seatNumber).push(seat);
      }
      
      
      for (const [_, seats] of numberMap) {
        if (seats.length > 1) {
          haveErrors = true;

          if (seatsPlan === CreatorStateService.seatsPlan) {
            for (const seat of seats) {
              const shapeElement = SeatManager.getHTMLElement(seat.guid);
              currentSeatsWithElements.push({ seat, element: shapeElement });
            }
          }
        }
      }
    }

    for (const { seat: prevSeat, element: prevElement } of previousSeatsWithElements) {
      if (!currentSeatsWithElements.some(curr => curr.seat.guid === prevSeat.guid)) {
        obs.push(
          ShapeManager.renewElement$(
            prevElement,
            prevSeat,
            {
              hasError: false,
              dragId: ShapeAttributesManager.getDragId(prevElement),
              position: 'absolute',
              showContextMenu: true
            }
          )
        );
      }
    }

    for (const { seat, element } of currentSeatsWithElements) {
      obs.push(
        ShapeManager.renewElement$(
          element,
          seat,
          {
            hasError: true,
            dragId: ShapeAttributesManager.getDragId(element),
            position: 'absolute',
            showContextMenu: true
          }
        )
      );
    }

    forkJoin(obs).subscribe();

    return haveErrors;
  }
}
